import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as Yup from 'yup';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';

import {
  Button,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core';

import { TextField } from '@mui/material';
import { DesktopDatePicker, LocalizationProvider } from '@mui/lab';

import AdapterDateFns from '@mui/lab/AdapterDateFns';

import moment from 'moment';
import { useHistory, useParams } from 'react-router';
import Input from '../../../components/Input';
import Datepicker from '../../../components/Datepicker';
import { useAxios } from '../../../utils/hooks';
import getValidationErrors from '../../../utils/getValidationErrors';
import { Form as FormModel } from '../../../models/form';
import { Record } from '../../../models/record';
import Loader from '../../../components/Loader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import Signatures from '../../../components/Signatures';
import { SignatureForm } from '../../../models/signature-form';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    background: 'white',
    padding: theme.spacing(3),
    margin: theme.spacing(3),
    marginBottom: theme.spacing(10),
  },
  formTitles: {
    fontWeight: 'bolder',
  },
  formControl: {
    margin: theme.spacing(2, 0),
  },
  divider: {
    margin: theme.spacing(2),
  },
}));

const SocialServiceForm: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const api = useAxios();
  const [form, setForm] = useState<FormModel | null>(null);
  const [signatures, setSignatures] = useState<SignatureForm[]>([]);
  const [record, setRecord] = useState<Record | null>(null);
  const [date, setDate] = useState(new Date());
  const { id, formId } = useParams<{ id: string; formId?: string }>();

  const formRef = useRef<FormHandles>(null);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  useEffect(() => {
    async function getRecordData() {
      try {
        const recordResponse = await api.current?.get(`/records/${id}`);
        setRecord(recordResponse?.data);

        if (formId) {
          const formResponse = await api.current?.get(`/forms/${formId}`);
          if (formResponse?.data)
            setDate(
              formResponse?.data.answers.date
                ? moment(formResponse?.data.answers.date).toDate()
                : new Date(),
            );
          setForm(formResponse?.data);

          if (
            formResponse?.data.answers &&
            formResponse?.data.answers.signatures
          ) {
            setSignatures(formResponse?.data.answers.signatures);
          }
        }
      } catch (error) {
        setAlert({
          isActive: true,
          type: 'error',
          message: 'Erro ao carregar prontuário.',
        });
      } finally {
        setLoading(false);
      }
    }

    getRecordData();
  }, [api, id, formId]);

  const resetAlert = () => {
    setAlert({
      isActive: false,
      type: 'info',
      message: 'none',
    });
  };

  const handleSubmit = useCallback(
    async (data: any) => {
      try {
        setLoading(true);
        resetAlert();
        formRef.current?.setErrors({});

        // const schema = Yup.object().shape({
        //   content: Yup.string().required('Insira uma nota'),
        // });

        // await schema.validate(data, { abortEarly: false });
        data.date = moment(date).format('YYYY-MM-DD');
        const formRequest = {
          type: 'socialService',
          title: 'Encaminhamento Serviço Social',
          patient: record?.patient._id,
          record: record?._id,
          answers: { ...data, signatures },
        };

        if (formId) {
          await api.current?.patch(`/forms/${formId}`, formRequest);
        } else {
          await api.current?.post('/forms', formRequest);
        }

        history.push(`/prontuarios/${id}`);
      } catch (error) {
        setLoading(false);
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
        } else {
          setAlert({
            isActive: true,
            type: 'error',
            message: 'Erro ao enviar encaminhamento.',
          });
        }
      }
    },
    [api, record, history, formId, date, id, signatures],
  );

  return (
    <>
      <Loader loading={loading} />

      {!loading && (
        <Form
          id="form"
          ref={formRef}
          initialData={form?.answers}
          onSubmit={handleSubmit}
          className={classes.root}
        >
          <FormControl fullWidth className={classes.formControl}>
            <Typography
              variant="h5"
              component="h1"
              align="center"
              className={classes.formTitles}
            >
              FICHA DE ENCAMINHAMENTO PARA O SERVIÇO SOCIAL
            </Typography>

            <Divider className={classes.divider} />
          </FormControl>

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Input id="author" label="Encaminhado por" name="author" />
            </Grid>

            <Grid item xs={6}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DesktopDatePicker
                  label="Data"
                  inputFormat="dd/MM/yyyy"
                  value={date}
                  onChange={(d) => setDate(d as Date)}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={6}>
              <Input id="phone" label="Telefone para contato" name="phone" />
            </Grid>

            <FormControl fullWidth className={classes.formControl}>
              <Divider className={classes.divider} />
            </FormControl>

            <Grid item xs={12}>
              <FormLabel className={classes.formTitles}>
                Dados do Paciente:
              </FormLabel>
            </Grid>

            <Grid item xs={12}>
              <Input id="patient_name" label="Nome" name="patient_name" />
            </Grid>

            <Grid item xs={6}>
              <Input
                id="patient_birth"
                label="Data de Nascimento"
                name="patient_birth"
              />
            </Grid>

            <Grid item xs={6}>
              <Input id="patient_age" label="Idade" name="patient_age" />
            </Grid>

            <Grid item xs={12}>
              <Input
                id="patient_father_name"
                label="Nome do pai"
                name="patient_father_name"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_father_age"
                label="Idade"
                name="patient_father_age"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_father_profession"
                label="Profissão"
                name="patient_father_profession"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_father_school"
                label="Escolaridade"
                name="patient_father_school"
              />
            </Grid>

            <Grid item xs={12}>
              <Input
                id="patient_mother_name"
                label="Nome da Mãe"
                name="patient_mother_name"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_mother_age"
                label="Idade"
                name="patient_mother_age"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_mother_profession"
                label="Profissão"
                name="patient_mother_profession"
              />
            </Grid>

            <Grid item xs={4}>
              <Input
                id="patient_mother_school"
                label="Escolaridade"
                name="patient_mother_school"
              />
            </Grid>
          </Grid>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Motivo do encaminhamento para o Serviço Social
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="forward"
                  label="Encaminhamento"
                  name="forward"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Avaliações e ou acompanhamentos já realizados ou em andamento
                </FormLabel>
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="professional"
                  label="Psiquiátrica (médico e telefone)"
                  name="professional"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="neural_doctor"
                  label="Neurológica (médico e telefone)"
                  name="neural_doctor"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="others"
                  label="Outras (médico e telefone)"
                  name="others"
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="observations"
                  label="Outras observações relevantes"
                  name="observations"
                  isMultiline
                  rows={5}
                />
              </Grid>

              <Grid item xs={12}>
                <Input
                  id="questions"
                  label="Questões a serem esclarecidas com o Serviço Social"
                  name="questions"
                  isMultiline
                  rows={5}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formControl}>
            <Divider className={classes.divider} />
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormLabel className={classes.formTitles}>
                  Assinaturas
                </FormLabel>
              </Grid>

              <Grid item xs={12} justify="center">
                <Signatures
                  formAnswers={form ? form.answers : {}}
                  onSignature={setSignatures}
                />
              </Grid>
            </Grid>
          </FormControl>

          <FormControl fullWidth className={classes.formTitles}>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push(`/prontuarios/${id}`)}
                >
                  Voltar
                </Button>
              </Grid>

              <Grid item>
                <Button type="submit" color="primary" variant="contained">
                  Enviar
                </Button>
              </Grid>
            </Grid>
          </FormControl>
        </Form>
      )}

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default SocialServiceForm;
